import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import LoggedOutPage from "./LoggedOutPage";
import { Alert, AlertProps, Box, Button, Stack } from "@mui/material";
import PasswordField from "./PasswordField";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import * as z from "zod";
import { zodResolver, displayError, generateDefaults } from "../utils/form";
import { useParams } from "react-router-dom";
import { findErrors } from "../utils/passwordTools";
import Loading from "./Loading";

function useValidationSchema() {
    const { formatMessage } = useIntl();

    return (
        z
            .object({
                newPassword: z.string().superRefine((value, ctx) => {
                    // find errors in the password and add them to the zod context
                    findErrors(window.passwordStrength, value).forEach((error) => {
                        ctx.addIssue({ code: "custom", message: formatMessage({ id: error.id }, error.values) });
                    });
                }),
                retypePassword: z.string(),
            })
            // check if passwords match
            .refine((data) => data.newPassword === data.retypePassword, {
                message: formatMessage({ id: "validation.password.match", defaultMessage: "Passwords must match" }),
                path: ["retypePassword"], // Attach error to retypePassword field
            })
    );
}

type FormInputs = z.infer<ReturnType<typeof useValidationSchema>>;

export default function PasswordReset() {
    const { token } = useParams();
    const schema = useValidationSchema();
    // a single alert when we are done (with success or permanent failure)
    const [alert, setAlert] = useState<{ severity: AlertProps["severity"]; label: string } | null>(null);
    // submit failed because of a server error - we allow a retry
    const [serverError, setServerError] = useState<string | null>(null);
    // token is checked
    const [tokenValid, setTokenValid] = useState(false);
    const {
        control,
        trigger,
        handleSubmit,
        formState: { errors },
    } = useForm<FormInputs>({
        resolver: zodResolver(schema),
        defaultValues: generateDefaults(schema),
        mode: "onChange",
    });

    useEffect(() => {
        setTokenValid(false);
        async function checkToken() {
            try {
                const response = await fetch(`/public/check_token/${token}`);
                if (response.ok) {
                    setTokenValid(true);
                } else {
                    setAlert({ severity: "error", label: "reset_password.invalid_token" });
                }
            } catch (error) {
                setAlert({ severity: "error", label: "reset_password.other_error" });
            }
        }

        checkToken();
    }, [token]);

    // display loading while checking token
    if (!tokenValid) {
        return (
            <LoggedOutPage title={<FormattedMessage id="reset_password.reset_form_title" />}>
                <Loading />
            </LoggedOutPage>
        );
    }

    // Handle form submission
    const onSubmit: SubmitHandler<FormInputs> = async (data) => {
        const response = await fetch("/api/reset_password/", {
            method: "POST",
            headers: {
                accept: "application/json",
                "content-type": "application/json",
            },
            body: JSON.stringify({
                password: data.newPassword,
                retype_password: data.retypePassword,
                token: token,
            }),
        });
        if (response.ok) {
            setAlert({ severity: "success", label: "reset_password.success_content" });
        } else if (response.status === 404) {
            setAlert({ severity: "error", label: "reset_password.invalid_token" });
        } else {
            setServerError("other_error");
        }
    };

    if (alert) {
        return (
            <LoggedOutPage title={<FormattedMessage id="reset_password.reset_form_title" />}>
                <Stack spacing={2} alignItems="center">
                    <Alert severity={alert.severity}>
                        <FormattedMessage id={alert.label} />
                    </Alert>
                    {alert.severity === "success" && (
                        <a href="/public/login">
                            <FormattedMessage id="go_to_login" />
                        </a>
                    )}
                </Stack>
            </LoggedOutPage>
        );
    }

    // Disable submit button if there are any errors
    const hasErrors = Object.keys(errors).length > 0;

    return (
        <LoggedOutPage title={<FormattedMessage id="reset_password.reset_form_title" />}>
            <Box component="form" noValidate onSubmit={handleSubmit(onSubmit)} width="30ch">
                <Stack spacing={2}>
                    <Controller
                        control={control}
                        name="newPassword"
                        render={({ field }) => (
                            <PasswordField
                                label="reset_password.password"
                                {...field}
                                autoComplete="new-password"
                                error={!!errors.newPassword}
                                helperText={errors.newPassword ? displayError(errors.newPassword) : null}
                                onChange={(ev) => {
                                    field.onChange(ev);
                                    trigger("retypePassword");
                                }}
                            />
                        )}
                    />

                    <Controller
                        control={control}
                        name="retypePassword"
                        render={({ field }) => (
                            <PasswordField
                                label="reset_password.retype_password"
                                {...field}
                                autoComplete="new-password"
                                error={!!errors.retypePassword}
                            />
                        )}
                    />

                    {serverError && (
                        <Alert severity="error">
                            <FormattedMessage id={`reset_password.${serverError}`} />
                        </Alert>
                    )}

                    <Button type="submit" variant="contained" disabled={hasErrors}>
                        <FormattedMessage id="submit" />
                    </Button>
                </Stack>
            </Box>
        </LoggedOutPage>
    );
}
